import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  Send, Lock, Pause, Building2, Mail, Phone, Tag, X, Clock, CheckCircle2, UserPlus, MessageSquare,
} from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card, SectionHeader } from '@/components/Card';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { useLabels } from '@/contexts/WorkspaceContext';
import { ticketsService, Ticket, TicketPriority, TicketStatus } from '@/services/tickets.service';
import { contactsService, Contact } from '@/services/contacts.service';
import { settingsService } from '@/services/settings.service';
import { departmentsService, Department } from '@/services/departments.service';
import {
  PRIORITY_META, STATUS_META, STATUS_ORDER, PRIORITY_ORDER, initialsFor, slaCountdownLabel,
  SLA_STATE_PILL, requesterLabel,
} from './ticketFormat';

type TeamMember = { userUid: string; firstName: string; lastName?: string; email: string };

export default function TicketDetail() {
  const { ticketUid } = useParams<{ ticketUid: string }>();
  const t = useLabels();
  const nav = useNavigate();

  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [contact, setContact] = useState<Contact | null>(null);
  const [tick, setTick] = useState(0);

  const load = async () => {
    if (!ticketUid) return;
    try {
      const r = await ticketsService.get(ticketUid);
      setTicket(r);
    } catch { /* interceptor toasts, 404 handled by empty state below */ } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setLoading(true); load(); }, [ticketUid]);
  useEffect(() => {
    settingsService.listTeam().then((r) => setTeam(r.map((u) => ({ userUid: u.userUid, firstName: u.firstName, lastName: u.lastName, email: u.email })))).catch(() => {});
    departmentsService.list().then(setDepartments).catch(() => {});
  }, []);
  useEffect(() => {
    if (ticket?.contactUid) contactsService.get(ticket.contactUid).then(setContact).catch(() => setContact(null));
    else setContact(null);
  }, [ticket?.contactUid]);
  // Re-render the SLA timers every 30s so the countdown stays live.
  useEffect(() => { const id = setInterval(() => setTick((v) => v + 1), 30_000); return () => clearInterval(id); }, []);

  const memberName = (uid?: string) => {
    if (!uid) return null;
    const m = team.find((x) => x.userUid === uid);
    return m ? [m.firstName, m.lastName].filter(Boolean).join(' ') || m.email : null;
  };

  const patch = async (payload: Parameters<typeof ticketsService.update>[1]) => {
    if (!ticketUid) return;
    try {
      const updated = await ticketsService.update(ticketUid, payload);
      setTicket(updated);
    } catch { /* interceptor toasts */ }
  };

  const setStatus = async (status: TicketStatus) => {
    if (!ticketUid) return;
    try {
      const updated = await ticketsService.setStatus(ticketUid, status);
      setTicket(updated);
      toast.success(`Marked ${STATUS_META[status].label.toLowerCase()}`);
    } catch { /* interceptor toasts */ }
  };

  if (loading) {
    return (
      <>
        <Topbar title="Loading…" breadcrumb={`${t('operations')} / ${t('tickets')}`} />
        <div className="w-full px-4 py-6 sm:px-7 sm:py-7 space-y-4">
          <div className="skeleton h-24 rounded-2xl" />
          <div className="skeleton h-64 rounded-2xl" />
        </div>
      </>
    );
  }

  if (!ticket) {
    return (
      <>
        <Topbar title="Not found" breadcrumb={`${t('operations')} / ${t('tickets')}`} />
        <div className="w-full px-4 py-6 sm:px-7 sm:py-7">
          <Card className="py-10 text-center text-sm text-text-muted">
            This {t('tickets', 'singular').toLowerCase()} doesn&apos;t exist or was removed.
            <div className="mt-3"><Button variant="secondary" onClick={() => nav('/tickets')}>Back to {t('tickets').toLowerCase()}</Button></div>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <Topbar
        title={`#${ticket.number} · ${ticket.subject}`}
        breadcrumb={`${t('operations')} / ${t('tickets')}`}
        right={
          <div className="flex flex-wrap items-center gap-1.5">
            {STATUS_ORDER.filter((s) => s !== ticket.status).map((s) => (
              <Button key={s} size="sm" variant="secondary" onClick={() => setStatus(s)}>
                {s === 'on_hold' && <Pause size={12} />}
                {s === 'resolved' && <CheckCircle2 size={12} />}
                {STATUS_META[s].label}
              </Button>
            ))}
          </div>
        }
      />

      <div className="w-full px-4 py-6 sm:px-7 sm:py-7">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
          {/* ── Main column: conversation ── */}
          <div className="space-y-4">
            <Card className="flex flex-wrap items-center gap-2">
              <span className={clsx('inline-flex items-center rounded-full px-2.5 py-0.5 text-2xs font-semibold', STATUS_META[ticket.status].pill)}>
                {STATUS_META[ticket.status].label}
              </span>
              <span className={clsx('inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-2xs font-semibold', PRIORITY_META[ticket.priority].pill)}>
                <span className={clsx('h-1.5 w-1.5 rounded-full', PRIORITY_META[ticket.priority].dot)} />
                {PRIORITY_META[ticket.priority].label}
              </span>
              {ticket.status === 'on_hold' && (
                <span className="inline-flex items-center gap-1 rounded-full bg-surface-alt px-2.5 py-0.5 text-2xs font-semibold text-text-muted">
                  <Pause size={10} /> Paused
                </span>
              )}
              <span className="ml-auto text-2xs text-text-muted">Created {new Date(ticket.createdAt).toLocaleString()}</span>
            </Card>

            <ConversationThread ticket={ticket} memberName={memberName} onSent={load} />

            <ActivityFeed ticket={ticket} memberName={memberName} />
          </div>

          {/* ── Side panel ── */}
          <div className="space-y-4">
            <Card>
              <SectionHeader title="Details" className="!mb-3" />
              <FormField label="Status">
                <Select value={ticket.status} onChange={(e) => setStatus(e.target.value as TicketStatus)}>
                  {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                </Select>
              </FormField>
              <FormField label="Priority">
                <Select value={ticket.priority} onChange={(e) => patch({ priority: e.target.value as TicketPriority })}>
                  {PRIORITY_ORDER.map((p) => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
                </Select>
              </FormField>
              <FormField label="Assignee">
                <Select value={ticket.assigneeUserUid ?? ''} onChange={(e) => patch({ assigneeUserUid: e.target.value || null })}>
                  <option value="">— unassigned —</option>
                  {team.map((m) => <option key={m.userUid} value={m.userUid}>{[m.firstName, m.lastName].filter(Boolean).join(' ') || m.email}</option>)}
                </Select>
              </FormField>
              <FormField label="Department">
                <Select value={ticket.departmentUid ?? ''} onChange={(e) => patch({ departmentUid: e.target.value || null })}>
                  <option value="">— none —</option>
                  {departments.map((d) => <option key={d.deptUid} value={d.deptUid}>{d.name}</option>)}
                </Select>
              </FormField>
              <FormField label="Category">
                <Input
                  defaultValue={ticket.category ?? ''}
                  onBlur={(e) => { if (e.target.value !== (ticket.category ?? '')) patch({ category: e.target.value }); }}
                  placeholder="Billing, Bug…"
                />
              </FormField>
              <TagsEditor tags={ticket.tags} onChange={(tags) => patch({ tags })} />
            </Card>

            <ClientCard ticket={ticket} contact={contact} />

            <SlaCard ticket={ticket} tick={tick} />
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Conversation thread + composer ─────────────────────────────────────────

function ConversationThread({
  ticket, memberName, onSent,
}: { ticket: Ticket; memberName: (uid?: string) => string | null; onSent: () => void }) {
  const [body, setBody] = useState('');
  const [internal, setInternal] = useState(false);
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await ticketsService.addComment(ticket.ticketUid, body.trim(), internal);
      setBody('');
      onSent();
    } catch { /* interceptor toasts */ } finally { setSending(false); }
  };

  return (
    <Card padded={false} className="overflow-hidden">
      <div className="border-b border-border px-5 py-3">
        <h3 className="flex items-center gap-1.5 font-display text-sm font-semibold text-text-primary">
          <MessageSquare size={14} /> Conversation
        </h3>
      </div>
      <div className="max-h-[480px] space-y-3 overflow-y-auto p-5">
        {ticket.description && (
          <div className="rounded-xl border border-border bg-surface-alt/50 p-3 text-sm text-text-secondary">
            <div className="mb-1 text-2xs font-semibold uppercase tracking-wide text-text-muted">Original request</div>
            {ticket.description}
          </div>
        )}
        {ticket.comments.length === 0 && !ticket.description && (
          <p className="py-8 text-center text-xs text-text-muted">No replies yet — start the conversation below.</p>
        )}
        {ticket.comments.map((c) => (
          <div
            key={c.commentUid}
            className={clsx(
              'rounded-xl border p-3 text-sm',
              c.internal ? 'border-warning/30 bg-warning-soft/40' : 'border-border bg-surface',
            )}
          >
            <div className="mb-1 flex items-center justify-between gap-2">
              <span className="inline-flex items-center gap-1.5 text-2xs font-semibold text-text-primary">
                {c.internal && <Lock size={11} className="text-warning" />}
                {memberName(c.authorUserUid) ?? 'Team member'}
                {c.internal && <span className="text-warning">· Internal note</span>}
              </span>
              <span className="text-2xs text-text-muted">{new Date(c.createdAt).toLocaleString()}</span>
            </div>
            <p className="whitespace-pre-wrap text-text-secondary">{c.body}</p>
          </div>
        ))}
      </div>
      <div className="border-t border-border p-4">
        <Textarea
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={internal ? 'Write an internal note (not visible to the requester)…' : 'Write a reply…'}
        />
        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="flex items-center rounded-lg border border-border bg-surface-alt p-0.5">
            <button
              type="button"
              onClick={() => setInternal(false)}
              className={clsx('rounded-md px-3 py-1.5 text-xs font-semibold transition', !internal ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted')}
            >
              Public reply
            </button>
            <button
              type="button"
              onClick={() => setInternal(true)}
              className={clsx('inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold transition', internal ? 'bg-warning-soft text-warning' : 'text-text-muted')}
            >
              <Lock size={11} /> Internal note
            </button>
          </div>
          <Button size="sm" variant="primary" iconLeft={<Send size={13} />} onClick={send} loading={sending} disabled={sending || !body.trim()}>
            Send
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ─── Activity feed ───────────────────────────────────────────────────────────

function activityText(e: Ticket['events'][number], memberName: (uid?: string) => string | null): string {
  const who = memberName(e.byUserUid) ?? 'Someone';
  switch (e.type) {
    case 'created': return `${who} created this ticket`;
    case 'status_changed': return `${who} changed status from ${e.fromValue ?? '—'} to ${e.toValue}`;
    case 'priority_changed': return `${who} changed priority from ${e.fromValue ?? '—'} to ${e.toValue}`;
    case 'assigned': return e.toValue ? `${who} assigned this to ${memberName(e.toValue) ?? e.toValue}` : `${who} unassigned this ticket`;
    case 'comment_added': return `${who} added a ${e.toValue} reply`;
    default: return `${who} updated this ticket`;
  }
}

function ActivityFeed({ ticket, memberName }: { ticket: Ticket; memberName: (uid?: string) => string | null }) {
  const events = useMemo(() => [...ticket.events].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()), [ticket.events]);
  if (events.length === 0) return null;
  return (
    <Card>
      <SectionHeader title="Activity" icon={<Clock size={14} />} className="!mb-3" />
      <ul className="space-y-2.5">
        {events.map((e, i) => (
          <li key={i} className="flex items-start gap-2 text-xs">
            <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-border" />
            <div>
              <span className="text-text-secondary">{activityText(e, memberName)}</span>
              <span className="ml-1.5 text-text-muted">{new Date(e.at).toLocaleString()}</span>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ─── Tags editor ─────────────────────────────────────────────────────────────

function TagsEditor({ tags, onChange }: { tags: string[]; onChange: (tags: string[]) => void }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v || tags.includes(v)) { setDraft(''); return; }
    onChange([...tags, v]);
    setDraft('');
  };
  return (
    <FormField label="Tags">
      <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-border bg-surface p-1.5">
        {tags.map((tag) => (
          <span key={tag} className="inline-flex items-center gap-1 rounded-full bg-surface-alt px-2 py-0.5 text-2xs text-text-secondary">
            <Tag size={9} /> {tag}
            <button onClick={() => onChange(tags.filter((x) => x !== tag))} className="text-text-muted hover:text-danger"><X size={10} /></button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          onBlur={add}
          placeholder="Add tag…"
          className="min-w-[80px] flex-1 bg-transparent px-1 py-0.5 text-xs text-text-primary outline-none placeholder:text-text-muted"
        />
      </div>
    </FormField>
  );
}

// ─── Client card ─────────────────────────────────────────────────────────────

function ClientCard({ ticket, contact }: { ticket: Ticket; contact: Contact | null }) {
  const name = contact ? [contact.firstName, contact.lastName].filter(Boolean).join(' ') : requesterLabel(ticket);
  const email = contact?.email ?? ticket.requesterEmail;
  const phone = contact?.phone ?? ticket.requesterPhone;
  return (
    <Card>
      <SectionHeader title="Client" icon={<UserPlus size={14} />} className="!mb-3" />
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-soft text-xs font-semibold text-primary">
          {initialsFor(name, email)}
        </span>
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-text-primary">{name}</div>
          {contact?.company && (
            <div className="flex items-center gap-1 truncate text-2xs text-text-muted"><Building2 size={10} /> {contact.company}</div>
          )}
        </div>
      </div>
      <div className="mt-3 space-y-1.5 text-xs text-text-secondary">
        {email && <div className="flex items-center gap-1.5"><Mail size={11} className="text-text-muted" /> {email}</div>}
        {phone && <div className="flex items-center gap-1.5"><Phone size={11} className="text-text-muted" /> {phone}</div>}
        {!email && !phone && <p className="text-text-muted">No contact details on file.</p>}
      </div>
    </Card>
  );
}

// ─── SLA card ────────────────────────────────────────────────────────────────

function SlaCard({ ticket }: { ticket: Ticket; tick: number }) {
  return (
    <Card>
      <SectionHeader title="SLA" icon={<Clock size={14} />} className="!mb-3" />
      <div className="space-y-3">
        <SlaRow label="First response" clock={ticket.sla.firstResponse} status={ticket.status} met={!!ticket.firstRespondedAt} />
        <SlaRow label="Resolution" clock={ticket.sla.resolution} status={ticket.status} met={!!ticket.resolvedAt} />
        {ticket.status === 'on_hold' && (
          <p className="flex items-center gap-1.5 rounded-lg bg-surface-alt px-2.5 py-1.5 text-2xs text-text-muted">
            <Pause size={11} /> Clock paused while on hold.
          </p>
        )}
      </div>
    </Card>
  );
}

function SlaRow({ label, clock, status, met }: { label: string; clock: Ticket['sla']['firstResponse']; status: TicketStatus; met: boolean }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs text-text-secondary">{label}</span>
      <span className={clsx('inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-2xs font-semibold', SLA_STATE_PILL[clock.state])}>
        {met && <CheckCircle2 size={10} />}
        {slaCountdownLabel(clock, status)}
      </span>
    </div>
  );
}
